import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, type SxProps, type Theme } from "@mui/material";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { type FC, type MouseEvent as ReactMouseEvent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Register, useNavigate } from "react-router-dom";
import { usePerformGlobalRefresh } from "../../../../components/header/hooks";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useJobColors } from "../../../../hooks/useJobColors";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import { type DepartmentLocation, EditDepartmentLocationConfigMode, NurseFullRole, type NurseRole, type RecurringShift } from "../../../../utils/models";
import { DEPARTMENT_TYPE_TO_COLOR } from "../../../../utils/utilFunctions";
import { SortableHeaderButton, type SortableHeaderButtonProps } from "../auxillary/SortableHeaderButton";
import { EditDepartmentLocationConfig } from "../EditDepartmentLocationConfig/EditDepartmentLocationConfig";
import type { LocationUpdateInfo } from "../EditDepartmentLocationConfig/useEditDepartmentLocationConfigState";
import { getOccupiedDays, gridCellStyle, gridCellTypography, gridColumnHeadersStyle, type ScheduleWeekData } from "../utils";
import { useLocationCellContext } from "./LocationCellContext";

type Props = {
	/**
	 * If the currentLocation is not provided - this will be treated as a location header cell
	 */
	currentLocation?: DepartmentLocation;
	isLocationOutsideOfConfig: boolean;
	departmentSpecificRoles: NurseRole[];
	/** The location's rendered week grid — the source of truth for which days actually have a placed shift. `null` for the header cell, which has no location. */
	weeklyShifts: ScheduleWeekData[] | null;
	refreshRecurringShifts: (options?: RefetchOptions) => Promise<
		QueryObserverResult<
			RecurringShift[],
			Register extends {
				defaultError: infer TError;
			}
				? TError
				: Error
		>
	>;
	/**
	 *  Optional props to render as a sortable header if no location is set */
	sortableHeaderButtonProps?: SortableHeaderButtonProps;
	uniqueSections: string[];
	isSynergyMS: boolean;
};

export const LocationCell: FC<Props> = ({
	isLocationOutsideOfConfig,
	currentLocation,
	departmentSpecificRoles,
	refreshRecurringShifts,
	weeklyShifts,
	sortableHeaderButtonProps,
	uniqueSections,
	isSynergyMS,
}) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { chosenDepartment } = useHospitalData();
	const jobColor = useJobColors();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const navigate = useNavigate();
	const { openLocationId, setOpenLocationId } = useLocationCellContext();
	const cellId =
		currentLocation != null
			? `location-cell-${currentLocation.id ?? `${currentLocation.location}_${currentLocation.shift_start}_${currentLocation.shift_end}_${(currentLocation.roles ?? []).join("_")}`}`
			: "location-cell-header";
	const isPopoverOpen = openLocationId === cellId;
	const rootRef = useRef<HTMLDivElement>(null);
	const didEditAnythingRef = useRef(false);

	// Occupancy comes straight from what the grid rendered (via `getOccupiedDays`), so the delete guard
	// and the cells agree: an expired recurring row that places no shift no longer blocks deletion.
	const occupiedDays = useMemo(() => getOccupiedDays(weeklyShifts ?? []), [weeklyShifts]);
	const [displayCloseWarning, setDisplayCloseWarning] = useState(false);
	const [isSomeDropdownOpen, setIsSomeDropdownOpen] = useState(false);

	const handleOnSomeDropdownOpenChanged = useCallback((isOpen: boolean) => {
		setIsSomeDropdownOpen(isOpen);
	}, []);

	const handleMarkDidEditAnything = useCallback(() => {
		didEditAnythingRef.current = true;
	}, []);

	const handleClose = useCallback(() => {
		setOpenLocationId(null);
	}, [setOpenLocationId]);

	const handleSave = useCallback(
		async (updateInfo?: LocationUpdateInfo) => {
			if (updateInfo) {
				if (updateInfo.didEdit) {
					await refreshRecurringShifts();
				}
				if (updateInfo.refreshDepartmentData) {
					await performGlobalRefresh({
						avoidRefreshFor: {
							nursesData: true,
							requestsData: true,
							notificationsData: true,
						},
					});
				}
			}
			showSnackbar(currentLocation && !isLocationOutsideOfConfig ? "Location Updated" : "Location Created", "success");
			handleClose();
		},
		[currentLocation, isLocationOutsideOfConfig, performGlobalRefresh, refreshRecurringShifts, handleClose, showSnackbar],
	);

	const handleOnClickOutside = useCallback(
		(e?: MouseEvent) => {
			if (isSomeDropdownOpen || !isPopoverOpen) {
				e?.stopPropagation();
				return;
			}
			if (didEditAnythingRef.current && !displayCloseWarning) {
				setDisplayCloseWarning(true);
				return;
			}
			handleClose();
			e?.stopPropagation();
		},
		[displayCloseWarning, handleClose, isSomeDropdownOpen, isPopoverOpen],
	);

	const handleCellClick = useCallback(
		(e: ReactMouseEvent) => {
			e.stopPropagation();
			if (isPopoverOpen) {
				handleOnClickOutside();
			} else {
				didEditAnythingRef.current = false;
				setOpenLocationId(cellId);
			}
		},
		[isPopoverOpen, handleOnClickOutside, setOpenLocationId, cellId],
	);

	const [isHovered, setIsHovered] = useState(false);

	return (
		<SystemReactTinyPopover
			isOpen={isPopoverOpen}
			onClickOutside={handleOnClickOutside}
			positions={["right"]}
			disabledChildrenWrapper={true}
			content={
				<Box>
					{
						<SystemDialog
							open={displayCloseWarning}
							onClose={() => {
								setDisplayCloseWarning(false);
							}}
							title={"Discard unsaved changes?"}
							body={"Your changes will be discarded"}
							onConfirm={() => {
								setDisplayCloseWarning(false);
								setOpenLocationId(null);
							}}
							cancel={"continue editing"}
							confirm={"discard changes"}
						/>
					}
					<EditDepartmentLocationConfig
						onSomeDropdownOpenChange={handleOnSomeDropdownOpenChanged}
						mode={isLocationOutsideOfConfig ? EditDepartmentLocationConfigMode.Create : EditDepartmentLocationConfigMode.Edit}
						existingConfig={currentLocation}
						onDidChangeNotifier={handleMarkDidEditAnything}
						onCancel={handleOnClickOutside}
						occupiedDays={occupiedDays}
						onSave={handleSave}
						uniqueSections={uniqueSections}
						isSynergyMS={isSynergyMS}
					/>
				</Box>
			}
		>
			{currentLocation != null ? (
				<InHouseTooltip title={"These location times are not part of your department config"} disableHoverListener={!isLocationOutsideOfConfig}>
					<Box
						ref={rootRef}
						data-testid={`master-schedule-location-cell-${currentLocation.id ?? "no-id"}`}
						sx={{
							...gridCellStyle,
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
							cursor: "pointer",
							width: "100%",
							gap: 0.5,
							pl: isSynergyMS ? 2 : undefined,
						}}
						onClick={handleCellClick}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "end",
									gap: 1,
									...gridCellTypography,
									color: "inherit",
									fontWeight: "inherit",
								}}
							>
								{isSynergyMS ? (
									currentLocation.departmentType ? (
										<InHouseTooltip key={currentLocation.id + currentLocation.departmentType} title={currentLocation.departmentType}>
											<Box
												sx={{
													background: DEPARTMENT_TYPE_TO_COLOR[currentLocation.departmentType],
													width: "6px",
													height: "16px",
													borderRadius: 2,
												}}
											/>
										</InHouseTooltip>
									) : null
								) : (
									<Box
										sx={{
											display: "flex",
											gap: 0.5,
										}}
									>
										{jobManagementEnabled && currentLocation.jobId
											? (() => {
													// Collect unique job IDs from location + ratio ladder entries
													const jobIdSet = new Set<string>([currentLocation.jobId]);
													for (const rl of currentLocation.ratioLadder ?? []) {
														if (rl.jobId) jobIdSet.add(rl.jobId);
													}
													return Array.from(jobIdSet).map((jid) => {
														const job = chosenDepartment.jobs?.find((j) => j.id === jid);
														return job ? (
															<InHouseTooltip key={jid} title={job.displayName}>
																<Box
																	sx={{
																		background: jobColor(job.id).dot,
																		width: "4px",
																		height: "12px",
																		borderRadius: 2,
																	}}
																/>
															</InHouseTooltip>
														) : null;
													});
												})()
											: (currentLocation.roles.includes("all")
													? departmentSpecificRoles.map((role) => NurseFullRole[role])
													: currentLocation.roles.filter((role) => role !== "all")
												)
													.sort()
													.map((role) => (
														<InHouseTooltip key={role} title={role}>
															<Box
																sx={{
																	background: roleColor(role).dot,
																	width: "4px",
																	height: "12px",
																	borderRadius: 2,
																}}
															/>
														</InHouseTooltip>
													))}
									</Box>
								)}
								{currentLocation.location || "No Location"}
								{isLocationOutsideOfConfig && (
									<span
										style={{
											color: "red",
											fontWeight: "inherit",
											fontSize: "16px",
										}}
									>
										{" "}
										*
									</span>
								)}
							</Box>
						</Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							{isSynergyMS && currentLocation.location && (
								<InHouseTooltip title={`Open ${currentLocation.location} facility page`}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											navigate(`/${chosenDepartment.department_id}/facilities/${currentLocation.id}/${encodeURIComponent(currentLocation.location)}`);
										}}
										sx={{
											opacity: isHovered ? 1 : 0,
											transition: "opacity 0.2s",
											p: 0.25,
										}}
									>
										<OpenInNewIcon fontSize={"inherit"} />
									</IconButton>
								</InHouseTooltip>
							)}
							<EditIcon
								fontSize={"inherit"}
								color={"inherit"}
								sx={{
									opacity: isHovered ? 1 : 0,
									transition: "opacity 0.2s",
								}}
							/>
						</Box>
					</Box>
				</InHouseTooltip>
			) : (
				<Box
					sx={
						[
							gridColumnHeadersStyle,
							(theme: Theme) => ({
								justifyContent: "space-between",
								cursor: "pointer",
								width: "100%",
								":hover": {
									backgroundColor: theme.palette.primary.main,
								},
							}),
						] as SxProps<Theme>
					}
					onClick={sortableHeaderButtonProps?.onSortClick}
					data-testid="master-schedule-location-filter"
				>
					{sortableHeaderButtonProps ? (
						<SortableHeaderButton injectStyle={false} label={t(isSynergyMS ? "Facility" : "Location")} sortOrder={sortableHeaderButtonProps.sortOrder} />
					) : (
						t(isSynergyMS ? "Facility" : "Location")
					)}
					{!chosenDepartment.disable_edit_master_schedule && (
						<IconButton size={"small"} onClick={handleCellClick} color={"inherit"} data-testid="master-schedule-add-location">
							<AddIcon fontSize={"small"} />
						</IconButton>
					)}
				</Box>
			)}
		</SystemReactTinyPopover>
	);
};
